import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Avatar,
  Link,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  FormControlLabel,
  Checkbox,
  Skeleton,
  Snackbar,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useLocalStorageState } from "../hooks/useLocalStorageState";
import {
  PendingChannel,
  WORKFLOW_STATUS,
  DISCOVERY_METHODS,
  OUTREACH_STATUS,
} from "../types/pendingChannels";
import { getAllPendingChannels } from "../services/firestoreChannelService";
import { EditPendingChannelModal } from "../components/pending/EditPendingChannelModal";
import { BulkApprovePendingChannelsModal } from "../components/pending/BulkApprovePendingChannelsModal";
import { BulkApprovalResultsModal } from "../components/pending/BulkApprovalResultsModal";
import { BulkRejectPendingChannelsModal } from "../components/pending/BulkRejectPendingChannelsModal";
import { BulkRejectionResultsModal } from "../components/pending/BulkRejectionResultsModal";
import { exportChannelsToCSV } from "../utils/csvExport";
import { useAuth } from "../contexts/AuthContext";

interface PendingOutreachState {
  channels: (PendingChannel & { _id: string })[];
  loading: boolean;
  error: string | null;
}

interface FilterState {
  discoveryMethod: string;
  status: string;
  outreach_status: string;
  subscriberCountMin: number | "";
  subscriberCountMax: number | "";
  discoveryDateFrom: string;
  discoveryDateTo: string;
  hasSocialLinks: boolean;
}

/**
 * PendingOutreach Page Component
 * Displays pending channels in a table format with filtering, sorting, and pagination
 */
const PendingOutreach: React.FC = () => {
  // State management
  const [state, setState] = useState<PendingOutreachState>({
    channels: [],
    loading: false,
    error: null,
  });

  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useLocalStorageState<GridSortModel>(
    "pending_outreach_sort_model",
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    useLocalStorageState<GridColumnVisibilityModel>(
      "pending_outreach_column_visibility",
      {},
    );

  // Filter state
  const [filters, setFilters] = useLocalStorageState<FilterState>(
    "pending_outreach_filters",
    {
      discoveryMethod: "",
      status: "",
      outreach_status: "",
      subscriberCountMin: "",
      subscriberCountMax: "",
      discoveryDateFrom: "",
      discoveryDateTo: "",
      hasSocialLinks: false,
    },
  );

  // Modal and approval state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<
    (PendingChannel & { _id: string }) | null
  >(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [bulkApprovalModalOpen, setBulkApprovalModalOpen] = useState(false);
  const [bulkApprovalResult, setBulkApprovalResult] = useState<any>(null);
  const [bulkApprovalResultsModalOpen, setBulkApprovalResultsModalOpen] =
    useState(false);
  const [bulkRejectionModalOpen, setBulkRejectionModalOpen] = useState(false);
  const [bulkRejectionResult, setBulkRejectionResult] = useState<any>(null);
  const [bulkRejectionResultsModalOpen, setBulkRejectionResultsModalOpen] =
    useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "info" });

  // Auth context
  const { currentUser } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Target tables for approval
  const TARGET_TABLES = ["youtube_reaction", "youtube_playlisting"];

  // Get filtered and sorted channels
  const filteredChannels = useMemo(() => {
    return state.channels.filter((channel) => {
      // Discovery Method filter
      if (
        filters.discoveryMethod &&
        channel.discovery_method !== filters.discoveryMethod
      ) {
        return false;
      }

      // Status filter
      if (filters.status && channel.workflow_status !== filters.status) {
        return false;
      }

      // Outreach Status filter
      if (
        filters.outreach_status &&
        channel.outreach_status !== filters.outreach_status
      ) {
        return false;
      }

      // Subscriber Count range filter
      const subscriberCount = Number(channel.subscriber_count) || 0;
      if (
        filters.subscriberCountMin !== "" &&
        subscriberCount < Number(filters.subscriberCountMin)
      ) {
        return false;
      }
      if (
        filters.subscriberCountMax !== "" &&
        subscriberCount > Number(filters.subscriberCountMax)
      ) {
        return false;
      }

      // Discovery Date range filter
      if (filters.discoveryDateFrom || filters.discoveryDateTo) {
        const discoveryDate =
          channel.discovery_date instanceof Date
            ? channel.discovery_date
            : new Date(channel.discovery_date);

        if (filters.discoveryDateFrom) {
          const fromDate = new Date(filters.discoveryDateFrom);
          if (discoveryDate < fromDate) {
            return false;
          }
        }

        if (filters.discoveryDateTo) {
          const toDate = new Date(filters.discoveryDateTo);
          // Set to end of day
          toDate.setHours(23, 59, 59, 999);
          if (discoveryDate > toDate) {
            return false;
          }
        }
      }

      // Has Social Links filter
      if (filters.hasSocialLinks) {
        const hasSocial = !!(
          (channel.social_media && channel.social_media.trim() !== "") ||
          (channel.socialLinks &&
            Object.values(channel.socialLinks).some((v) => v))
        );
        if (!hasSocial) {
          return false;
        }
      }

      return true;
    });
  }, [state.channels, filters]);

  // Reset filters handler
  const handleResetFilters = useCallback(() => {
    setFilters({
      discoveryMethod: "",
      status: "",
      outreach_status: "",
      subscriberCountMin: "",
      subscriberCountMax: "",
      discoveryDateFrom: "",
      discoveryDateTo: "",
      hasSocialLinks: false,
    });
  }, [setFilters]);

  // Check if any filters are applied
  const hasActiveFilters = useMemo(() => {
    return (
      filters.discoveryMethod !== "" ||
      filters.status !== "" ||
      filters.outreach_status !== "" ||
      filters.subscriberCountMin !== "" ||
      filters.subscriberCountMax !== "" ||
      filters.discoveryDateFrom !== "" ||
      filters.discoveryDateTo !== "" ||
      filters.hasSocialLinks
    );
  }, [filters]);

  // Format numbers with abbreviations
  const formatNumber = (num: any): string => {
    if (num === undefined || num === null || num === "") {
      return String(num);
    }

    let value: number;
    try {
      value = typeof num === "string" ? parseFloat(num) : Number(num);
    } catch (e) {
      return String(num);
    }

    if (isNaN(value)) {
      return String(num);
    }

    if (value >= 1000000000) {
      return (value / 1000000000).toFixed(1).replace(/\.0$/, "") + "B";
    } else if (value >= 1000000) {
      return (value / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1).replace(/\.0$/, "") + "K";
    } else {
      return value.toLocaleString();
    }
  };

  // Fetch pending channels
  const fetchPendingChannels = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const channels = await getAllPendingChannels();
      setState({
        channels,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error("Error fetching pending channels:", error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: "Failed to load pending channels. Please try again.",
      }));
    }
  }, []);

  // Modal handlers
  const handleOpenEditModal = useCallback(
    (channel: PendingChannel & { _id: string }) => {
      setSelectedChannel(channel);
      setEditModalOpen(true);
      setModalError(null);
    },
    [],
  );

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setSelectedChannel(null);
    setModalError(null);
  }, []);

  const handleModalSave = useCallback(
    async (channelId: string, updates: Partial<PendingChannel>) => {
      setModalLoading(true);
      setModalError(null);
      try {
        // Refresh data after save
        await fetchPendingChannels();
        setSnackbar({
          open: true,
          message: "Channel updated successfully",
          severity: "success",
        });
        handleCloseEditModal();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to save changes";
        setModalError(errorMessage);
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      } finally {
        setModalLoading(false);
      }
    },
    [fetchPendingChannels, handleCloseEditModal],
  );

  const handleModalApprove = useCallback(
    async (channelId: string, updates: Partial<PendingChannel>) => {
      try {
        // Refresh data after approval
        await fetchPendingChannels();
        setSnackbar({
          open: true,
          message: "Channel approved successfully!",
          severity: "success",
        });
        handleCloseEditModal();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to approve channel";
        console.error("Error in approval callback:", error);
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      }
    },
    [fetchPendingChannels, handleCloseEditModal],
  );

  const handleActionClick = useCallback(
    (channelId: string, action: "edit" | "approve" | "reject") => {
      const channel = state.channels.find((ch) => ch._id === channelId);
      if (channel) {
        if (action === "edit") {
          handleOpenEditModal(channel);
        }
        // Approve and reject actions are handled via the modal
      }
    },
    [state.channels, handleOpenEditModal],
  );

  const handleBulkApprovalOpen = useCallback(() => {
    if (selectedRows.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select channels to approve",
        severity: "warning",
      });
      return;
    }
    setBulkApprovalModalOpen(true);
  }, [selectedRows]);

  const handleBulkApprovalClose = useCallback(() => {
    setBulkApprovalModalOpen(false);
  }, []);

  const handleBulkApprovalSuccess = useCallback(
    async (result: any) => {
      // Store result and show modal
      setBulkApprovalResult(result);
      setBulkApprovalResultsModalOpen(true);

      // Refresh data after bulk approval
      await fetchPendingChannels();

      // Clear selection
      setSelectedRows([]);
    },
    [fetchPendingChannels],
  );

  const handleBulkRejectionOpen = useCallback(() => {
    if (selectedRows.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select channels to reject",
        severity: "warning",
      });
      return;
    }
    setBulkRejectionModalOpen(true);
  }, [selectedRows]);

  const handleBulkRejectionClose = useCallback(() => {
    setBulkRejectionModalOpen(false);
  }, []);

  const handleBulkRejectionSuccess = useCallback(
    async (result: any) => {
      // Store result and show modal
      setBulkRejectionResult(result);
      setBulkRejectionResultsModalOpen(true);

      // Refresh data after bulk rejection
      await fetchPendingChannels();

      // Clear selection
      setSelectedRows([]);
    },
    [fetchPendingChannels],
  );

  const handleExportCSV = useCallback(() => {
    if (selectedRows.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select channels to export",
        severity: "warning",
      });
      return;
    }

    try {
      const selectedChannels = state.channels.filter((ch) =>
        selectedRows.includes(ch._id),
      );
      const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
      const filename = `pending-channels-${timestamp}.csv`;

      exportChannelsToCSV(selectedChannels, filename);

      setSnackbar({
        open: true,
        message: `Successfully exported ${selectedChannels.length} channel(s) to CSV`,
        severity: "success",
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to export CSV";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  }, [selectedRows, state.channels]);

  // Load channels on component mount
  useEffect(() => {
    fetchPendingChannels();
  }, [fetchPendingChannels]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    fetchPendingChannels();
  }, [fetchPendingChannels]);

  // Get status color
  const getStatusColor = (
    status: string,
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "warning"
    | "info"
    | "success" => {
    switch (status) {
      case WORKFLOW_STATUS.PENDING:
        return "warning";
      case WORKFLOW_STATUS.REVIEW:
        return "info";
      case WORKFLOW_STATUS.APPROVED:
        return "success";
      case WORKFLOW_STATUS.REJECTED:
        return "error";
      case WORKFLOW_STATUS.CONTACTED:
        return "secondary";
      default:
        return "default";
    }
  };

  // Get outreach status color
  const getOutreachStatusColor = (
    status: string,
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "warning"
    | "info"
    | "success" => {
    switch (status) {
      case OUTREACH_STATUS.PENDING:
        return "warning";
      case OUTREACH_STATUS.CONTACTED:
        return "info";
      case OUTREACH_STATUS.RESPONDED:
        return "success";
      case OUTREACH_STATUS.NOT_INTERESTED:
        return "error";
      case OUTREACH_STATUS.CONVERTED:
        return "success";
      default:
        return "default";
    }
  };

  // Get discovery method display name
  const getDiscoveryMethodDisplay = (method: string): string => {
    const methodMap: Record<string, string> = {
      [DISCOVERY_METHODS.CURATOR_DISCOVERY]: "Curator Discovery",
      [DISCOVERY_METHODS.SEARCH]: "Search",
      [DISCOVERY_METHODS.REFERRAL]: "Referral",
      [DISCOVERY_METHODS.MANUAL]: "Manual",
      [DISCOVERY_METHODS.IMPORT]: "Import",
      [DISCOVERY_METHODS.API]: "API",
    };
    return methodMap[method] || method;
  };

  // Format date
  const formatDate = (date: any): string => {
    console.log(date);
    if (!date) return "-";
    try {
      let d: Date;
      
      // Handle Firestore Timestamp objects
      if (date && typeof date === 'object' && 'toDate' in date) {
        d = date.toDate();
      } else if (date instanceof Date) {
        d = date;
      } else {
        d = new Date(date);
      }
      
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "-";
    }
  };

  // Define table columns
  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "Channel Name",
      flex: 1,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {params.row.url && (
            <Avatar
              src={`https://www.youtube.com/favicon.ico`}
              alt={params.row.title}
              sx={{ width: 16, height: 16 }}
            />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" noWrap>
              <Link
                href={params.row.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: "0.75rem", textDecoration: "none" }}
              >
                {params.row.title}
              </Link>
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "subscriber_count",
      headerName: "Subscribers",
      width: 130,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatNumber(params.row.subscriber_count)}
        </Typography>
      ),
    },
    {
      field: "playlist_count",
      headerName: "Playlists",
      width: 110,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatNumber(params.row.playlist_count)}
        </Typography>
      ),
    },
    {
      field: "email",
      headerName: "Contact Info",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {params.row.email && (
            <Link
              href={`mailto:${params.row.email}`}
              sx={{ fontSize: "0.875rem" }}
            >
              {params.row.email}
            </Link>
          )}
          {!params.row.email && <Typography variant="caption">-</Typography>}
        </Box>
      ),
    },
    /*   {
      field: "discovery_method",
      headerName: "Discovery Method",
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getDiscoveryMethodDisplay(params.row.discovery_method)}
          size="small"
          variant="outlined"
        />
      ),
    }, */
    {
      field: "discovery_date",
      headerName: "Discovery Date",
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatDate(params.row.discovery_date)}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Edit Channel">
            <IconButton
              size="small"
              color="primary"
              onClick={() =>
                handleActionClick(params.row._id || params.row.id, "edit")
              }
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Approve">
            <IconButton
              size="small"
              color="success"
              onClick={() =>
                handleActionClick(params.row._id || params.row.id, "approve")
              }
            >
              <CheckCircleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject">
            <IconButton
              size="small"
              color="error"
              onClick={() =>
                handleActionClick(params.row._id || params.row.id, "reject")
              }
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          Pending Outreach Channels
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and approve channels discovered for outreach campaigns
        </Typography>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flex: 1,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={state.loading}
          >
            Add Channel
          </Button>

          {selectedRows.length > 0 && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {selectedRows.length} selected
              </Typography>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircleIcon />}
                disabled={state.loading}
                onClick={handleBulkApprovalOpen}
              >
                Bulk Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<CancelIcon />}
                disabled={state.loading}
                onClick={handleBulkRejectionOpen}
              >
                Reject
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<FileDownloadIcon />}
                disabled={state.loading}
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
            </Box>
          )}
        </Box>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={handleRefresh}
            disabled={state.loading}
            size="small"
          >
            {state.loading ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          {hasActiveFilters && (
            <Button size="small" variant="text" onClick={handleResetFilters}>
              Reset
            </Button>
          )}
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ flexWrap: "wrap", alignItems: "flex-start" }}
        >
          {/* Discovery Method Filter */}
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>Discovery Method</InputLabel>
            <Select
              value={filters.discoveryMethod}
              onChange={(e) =>
                setFilters({ ...filters, discoveryMethod: e.target.value })
              }
              label="Discovery Method"
            >
              <MenuItem value="">All Methods</MenuItem>
              <MenuItem value={DISCOVERY_METHODS.CURATOR_DISCOVERY}>
                Curator Discovery
              </MenuItem>
              <MenuItem value={DISCOVERY_METHODS.SEARCH}>Search</MenuItem>
              <MenuItem value={DISCOVERY_METHODS.REFERRAL}>Referral</MenuItem>
              <MenuItem value={DISCOVERY_METHODS.MANUAL}>Manual</MenuItem>
              <MenuItem value={DISCOVERY_METHODS.IMPORT}>Import</MenuItem>
              <MenuItem value={DISCOVERY_METHODS.API}>API</MenuItem>
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl sx={{ minWidth: 140 }} size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              label="Status"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value={WORKFLOW_STATUS.PENDING}>Pending</MenuItem>
              <MenuItem value={WORKFLOW_STATUS.REVIEW}>Review</MenuItem>
              <MenuItem value={WORKFLOW_STATUS.APPROVED}>Approved</MenuItem>
              <MenuItem value={WORKFLOW_STATUS.REJECTED}>Rejected</MenuItem>
              <MenuItem value={WORKFLOW_STATUS.CONTACTED}>Contacted</MenuItem>
            </Select>
          </FormControl>

          {/* Outreach Status Filter */}
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel>Outreach Status</InputLabel>
            <Select
              value={filters.outreach_status}
              onChange={(e) =>
                setFilters({ ...filters, outreach_status: e.target.value })
              }
              label="Outreach Status"
            >
              <MenuItem value="">All Outreach Status</MenuItem>
              <MenuItem value={OUTREACH_STATUS.PENDING}>Pending</MenuItem>
              <MenuItem value={OUTREACH_STATUS.CONTACTED}>Contacted</MenuItem>
              <MenuItem value={OUTREACH_STATUS.RESPONDED}>Responded</MenuItem>
              <MenuItem value={OUTREACH_STATUS.NOT_INTERESTED}>
                Not Interested
              </MenuItem>
              <MenuItem value={OUTREACH_STATUS.CONVERTED}>Converted</MenuItem>
            </Select>
          </FormControl>

          {/* Subscriber Count Min */}
          <TextField
            label="Min Subscribers"
            type="number"
            size="small"
            value={filters.subscriberCountMin}
            onChange={(e) =>
              setFilters({
                ...filters,
                subscriberCountMin: e.target.value
                  ? Number(e.target.value)
                  : "",
              })
            }
            sx={{ width: 140 }}
            inputProps={{ inputMode: "numeric" }}
          />

          {/* Subscriber Count Max */}
          <TextField
            label="Max Subscribers"
            type="number"
            size="small"
            value={filters.subscriberCountMax}
            onChange={(e) =>
              setFilters({
                ...filters,
                subscriberCountMax: e.target.value
                  ? Number(e.target.value)
                  : "",
              })
            }
            sx={{ width: 140 }}
            inputProps={{ inputMode: "numeric" }}
          />

          {/* Discovery Date From */}
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={filters.discoveryDateFrom}
            onChange={(e) =>
              setFilters({ ...filters, discoveryDateFrom: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />

          {/* Discovery Date To */}
          <TextField
            label="To Date"
            type="date"
            size="small"
            value={filters.discoveryDateTo}
            onChange={(e) =>
              setFilters({ ...filters, discoveryDateTo: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />

          {/* Has Social Links */}
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.hasSocialLinks}
                onChange={(e) =>
                  setFilters({ ...filters, hasSocialLinks: e.target.checked })
                }
              />
            }
            label="Has Social Links"
          />
        </Stack>
      </Box>

      {/* Error Alert */}
      {state.error && (
        <Alert
          severity="error"
          onClose={() => setState((prev) => ({ ...prev, error: null }))}
          sx={{ mb: 3 }}
        >
          {state.error}
        </Alert>
      )}

      {/* Loading Skeleton */}
      {state.loading && state.channels.length === 0 && (
        <Box sx={{ width: "100%" }}>
          {[...Array(5)].map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={60}
              sx={{ mb: 1 }}
            />
          ))}
        </Box>
      )}

      {/* Data Grid */}
      {!(state.loading && state.channels.length === 0) && (
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={filteredChannels.map((ch) => ({ ...ch, id: ch._id }))}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
              sorting: {
                sortModel: sortModel || [],
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            columnVisibilityModel={columnVisibility}
            onSortModelChange={(newModel) => setSortModel(newModel)}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibility(newModel)
            }
            checkboxSelection
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={(newSelection) =>
              setSelectedRows(newSelection)
            }
            loading={state.loading}
            sx={{
              "& .MuiDataGrid-cell": {
                borderColor: theme.palette.divider,
              },
              "& .MuiDataGrid-row": {
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              },
              // Responsive font sizes
              [theme.breakpoints.down("sm")]: {
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  padding: "8px 4px",
                },
                "& .MuiDataGrid-columnHeader": {
                  fontSize: "0.75rem",
                  padding: "8px 4px",
                },
              },
            }}
          />
        </Box>
      )}

      {/* Empty State */}
      {!state.loading && state.channels.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No pending channels found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Start by adding a new channel or importing from discovery sources
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />}>
            Add First Channel
          </Button>
        </Box>
      )}

      {/* No Results State (with filters applied) */}
      {!state.loading &&
        state.channels.length > 0 &&
        filteredChannels.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No channels match your filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try adjusting your filter criteria
            </Typography>
            <Button variant="outlined" onClick={handleResetFilters}>
              Reset Filters
            </Button>
          </Box>
        )}

      {/* Edit Pending Channel Modal */}
      <EditPendingChannelModal
        open={editModalOpen}
        channel={selectedChannel}
        onClose={handleCloseEditModal}
        onSave={handleModalSave}
        onApprove={handleModalApprove}
        loading={modalLoading}
        error={modalError}
        targetTables={TARGET_TABLES}
      />

      {/* Bulk Approval Modal */}
      <BulkApprovePendingChannelsModal
        open={bulkApprovalModalOpen}
        channels={state.channels.filter((ch) => selectedRows.includes(ch._id))}
        onClose={handleBulkApprovalClose}
        onSuccess={handleBulkApprovalSuccess}
        targetTables={TARGET_TABLES}
      />

      {/* Bulk Approval Results Modal */}
      <BulkApprovalResultsModal
        open={bulkApprovalResultsModalOpen}
        result={bulkApprovalResult}
        onClose={() => setBulkApprovalResultsModalOpen(false)}
      />

      {/* Bulk Rejection Modal */}
      <BulkRejectPendingChannelsModal
        open={bulkRejectionModalOpen}
        channels={state.channels.filter((ch) => selectedRows.includes(ch._id))}
        onClose={handleBulkRejectionClose}
        onSuccess={handleBulkRejectionSuccess}
      />

      {/* Bulk Rejection Results Modal */}
      <BulkRejectionResultsModal
        open={bulkRejectionResultsModalOpen}
        result={bulkRejectionResult}
        onClose={() => setBulkRejectionResultsModalOpen(false)}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PendingOutreach;
